/**
 * Leasing.ai Pixel Block
 *
 * Injection block that loads the leasing.ai widget loader and fires the
 * hidden image pixel. The custom token holds the official snippet (script +
 * img), either URL, or a bare pid — we extract the pid and inject canonical
 * nodes rather than re-inserting pasted HTML.
 */

import { useEffect } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import { useTrackingAllowed } from '../../shared/analytics';
import { buildLoaderSrc, buildPixelSrc, extractLeasingAiPid } from './resolve';

const SCRIPT_ID = 'leasing-ai-pixel-script';
const FIRED_PIXELS_KEY = '__vibesitesLeasingAiPixels';

function getFiredPixels(): Set<string> {
  const win = window as Window & { [FIRED_PIXELS_KEY]?: Set<string> };
  if (!win[FIRED_PIXELS_KEY]) win[FIRED_PIXELS_KEY] = new Set();
  return win[FIRED_PIXELS_KEY]!;
}

function ensureLoader(pid: string) {
  if (document.getElementById(SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = buildLoaderSrc(pid);
  script.async = true;
  document.head.appendChild(script);
}

function fireImagePixel(pid: string) {
  const fireKey = `${window.location.pathname}|${pid}`;
  const firedPixels = getFiredPixels();
  if (firedPixels.has(fireKey)) return;
  firedPixels.add(fireKey);

  const img = document.createElement('img');
  img.src = buildPixelSrc(pid);
  img.dataset.vibesitesBlock = 'leasing-ai-pixel';
  img.width = 1;
  img.height = 1;
  img.alt = '';
  img.style.display = 'none';
  document.body.appendChild(img);
}

function LeasingAiPixelComponent(_props: BlockProps) {
  const pid = extractLeasingAiPid(
    useBlockToken('pixel', { defaultField: 'custom_leasing_ai' }),
  );
  const trackingAllowed = useTrackingAllowed('advertising');

  useEffect(() => {
    if (!trackingAllowed || !pid) return;
    ensureLoader(pid);
    fireImagePixel(pid);
  }, [trackingAllowed, pid]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'LeasingAiPixel',
  displayName: 'Leasing.ai Pixel',
  tokens: {
    pixel: {
      label: 'Leasing.ai snippet or property ID',
      type: 'text',
      defaultField: 'custom_leasing_ai',
      suggestedFields: ['custom_leasing_ai'],
      description:
        'Official leasing.ai snippet (loader script + hidden image pixel), either URL, or the bare pid. Full snippets are parsed for the pid. Maps to custom_leasing_ai by default.',
      required: true,
    },
  },
};

export const leasingAiPixelBlock: BlockDefinition = {
  id: 'leasing-ai-pixel',
  name: 'Leasing.ai Pixel',
  description:
    'Leasing.ai widget loader and tracking pixel. Reads the official snippet from custom_leasing_ai and injects the loader script plus hidden image beacon.',
  category: 'Analytics',
  placement: 'injection',
  defaultScope: 'global',
  component: LeasingAiPixelComponent,
  contentSchema,
  tags: ['leasing.ai', 'leasingai', 'pixel', 'tracking', 'ads', 'widget'],
};
